import Head from 'next/head'
import dynamic from 'next/dynamic'
import { createContext, useContext } from 'react'

import { FederatedComponentBoundary } from '@fe-toolkit/web-components/ssr'

import FallbackHeader from '@/components/fallbacks/FallbackHeader'
import FallbackFooter from '@/components/fallbacks/FallbackFooter'
import HeaderFlagsContext from '@/utils/headerFlags/HeaderFlagsContext'

import type { ReactNode } from 'react'

// next/dynamic does not pass props to `loading`, so the member name reaches the
// loading header through context. Without it, signed-in members get a
// logged-out header in the server HTML until fe-common loads.
const MemberNameContext = createContext<string | undefined>(undefined)

function LoadingHeader() {
  const headerFlags = useContext(HeaderFlagsContext)

  return (
    <FallbackHeader {...headerFlags} username={useContext(MemberNameContext)} />
  )
}

function LoadingFooter() {
  const { isBrandRefresh } = useContext(HeaderFlagsContext)

  return <FallbackFooter isBrandRefresh={isBrandRefresh} />
}

// Client-only because this app uses @module-federation/nextjs-mf and fe-common
// uses @module-federation/modern-js-v3. The two runtimes don't share the react
// singleton during SSR, which crashes with "Cannot read properties of null
// (reading 'useState')". The fallbacks paint while the remote chunk loads.
const CommonHeader = dynamic(
  () => import('fe_common/components/CommonHeader'),
  {
    ssr: false,
    loading: LoadingHeader,
  },
)

const CommonFooter = dynamic(
  () => import('fe_common/components/CommonFooter'),
  {
    ssr: false,
    loading: LoadingFooter,
  },
)

export default function MainLayout({
  children,
  title = `We're up to good`,
  description = 'Doing good is our thing. Our super SIM plans, stonking refurbished phone deals plus Fixed UK prices for 2023 are all good news for your wallet. Find out more',
  memberDetails,
}: Readonly<{
  children?: ReactNode
  title?: string
  description?: string
  memberDetails?: { memberName?: string }
}>) {
  const headerFlags = useContext(HeaderFlagsContext)

  return (
    <>
      <Head>
        <title>{`${title} | giffgaff`}</title>
        <meta content={description} name="description"></meta>
      </Head>
      <MemberNameContext.Provider value={memberDetails?.memberName}>
        <FederatedComponentBoundary
          fallback={
            <FallbackHeader
              {...headerFlags}
              username={memberDetails?.memberName}
            />
          }
          logLabel="fe_common Header"
        >
          <CommonHeader username={memberDetails?.memberName} {...headerFlags} />
        </FederatedComponentBoundary>
      </MemberNameContext.Provider>
      <main id="main-content">{children}</main>
      <FederatedComponentBoundary
        fallback={
          <FallbackFooter isBrandRefresh={headerFlags.isBrandRefresh} />
        }
        logLabel="fe_common Footer"
      >
        {/* fe-common's CommonFooter spreads unknown props onto its legacy Footer, so the prereg flag stays out. */}
        <CommonFooter
          isBlackFriday={headerFlags.isBlackFriday}
          isBrandRefresh={headerFlags.isBrandRefresh}
        />
      </FederatedComponentBoundary>
    </>
  )
}
